import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getPaymentHistory, retryPayment } from '../../redux/slices/fidelityPaymentSlice';
import { RootState } from '../../redux/store';
import FidelityPaymentService from '../../services/fidelityPaymentService';
import {
  RefreshCw,
  Download,
  ChevronLeft,
  ChevronRight,
  X,
  AlertCircle,
  RotateCcw,
  Eye,
  Filter,
  Inbox,
} from 'lucide-react';

interface Payment {
  paymentId: string;
  transactionRef: string;
  amount: number;
  status: 'Successful' | 'Failed' | 'Processing' | 'Pending';
  createdAt: string;
  completedAt?: string;
}

interface Pagination {
  page: number;
  pages: number;
  total: number;
  limit: number;
}

interface FidelityPaymentState {
  paymentHistory: Payment[];
  pagination: Pagination;
  historyLoading: boolean;
  historyError: string | null;
}

const statusConfig: Record<string, { label: string; badgeClass: string }> = {
  Successful: { label: 'Successful', badgeClass: 'badge-success' },
  Failed: { label: 'Failed', badgeClass: 'badge-danger' },
  Processing: { label: 'Processing', badgeClass: 'badge-warning' },
  Pending: { label: 'Pending', badgeClass: 'badge-info' },
};

const PaymentHistory: React.FC = () => {
  const dispatch = useDispatch();
  const {
    paymentHistory,
    pagination,
    historyLoading,
    historyError,
  } = useSelector((state: RootState) => state.fidelityPayment) as FidelityPaymentState;

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);

  const downloadReceipt = async (payment: Payment) => {
    await FidelityPaymentService.downloadFundingReceipt(payment.paymentId);
  };

  useEffect(() => {
    dispatch(getPaymentHistory({
      page: currentPage,
      limit: 10,
      status: statusFilter || undefined,
    }) as any);
  }, [dispatch, currentPage, statusFilter]);

  const handlePageChange = (newPage: number): void => {
    if (newPage > 0 && newPage <= pagination.pages) {
      setCurrentPage(newPage);
    }
  };

  const handleRetry = (paymentId: string): void => {
    if (window.confirm('Are you sure you want to retry this payment?')) {
      dispatch(retryPayment(paymentId) as any);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <div className="card overflow-hidden">
      {/* Table Header */}
      <div className="card-header flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-fintech-text">Payment History</h2>
          <p className="text-xs text-fintech-textMuted mt-0.5">
            {pagination.total > 0 ? `${pagination.total} total transactions` : 'Your payment transactions'}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Status Filter */}
          <div className="relative flex items-center gap-1.5">
            <Filter size={14} className="text-fintech-textMuted" />
            <select
              id="statusFilter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="form-select py-1.5 text-xs pr-8 min-w-[130px]"
              aria-label="Filter by status"
            >
              <option value="">All Statuses</option>
              <option value="Successful">Successful</option>
              <option value="Failed">Failed</option>
              <option value="Processing">Processing</option>
              <option value="Pending">Pending</option>
            </select>
          </div>

          {/* Refresh */}
          <button
            className="p-1.5 rounded-lg text-fintech-textMuted hover:bg-slate-100 transition-colors"
            onClick={() => dispatch(getPaymentHistory({ page: currentPage, limit: 10, status: statusFilter || undefined }) as any)}
            title="Refresh"
            aria-label="Refresh payment history"
          >
            <RefreshCw size={14} className={historyLoading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Error State */}
      {historyError && (
        <div className="mx-6 mt-4">
          <div className="alert alert-error">
            <AlertCircle size={16} className="flex-shrink-0" />
            <span>Error loading payment history: {historyError}</span>
          </div>
        </div>
      )}

      {/* Loading State */}
      {historyLoading && paymentHistory.length === 0 ? (
        <div className="p-8 text-center">
          <div className="spinner mx-auto mb-3" />
          <p className="text-sm text-fintech-textMuted">Loading payment history...</p>
        </div>
      ) : paymentHistory.length === 0 ? (
        /* Empty State */
        <div className="p-12 text-center">
          <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Inbox size={24} className="text-fintech-textMuted" />
          </div>
          <h3 className="text-sm font-semibold text-fintech-text mb-1">No payment records found</h3>
          <p className="text-xs text-fintech-textMuted">
            {statusFilter ? `No ${statusFilter.toLowerCase()} payments found.` : 'Your payment history will appear here.'}
          </p>
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th className="text-right">Amount</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Completed</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paymentHistory.map((payment: Payment) => {
                  const status = statusConfig[payment.status] || { label: payment.status, badgeClass: 'badge-secondary' };
                  return (
                    <tr key={payment.paymentId}>
                      <td>
                        <span className="font-mono text-xs text-fintech-textMuted bg-slate-50 px-2 py-0.5 rounded border border-slate-200">
                          {payment.transactionRef}
                        </span>
                      </td>
                      <td className="numeric font-semibold text-fintech-text">
                        {formatCurrency(payment.amount)}
                      </td>
                      <td>
                        <span className={`badge ${status.badgeClass}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="text-sm text-fintech-textMuted">
                        {formatDate(payment.createdAt)}
                      </td>
                      <td className="text-sm text-fintech-textMuted">
                        {payment.completedAt ? formatDate(payment.completedAt) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-1.5">
                          {payment.status === 'Failed' && (
                            <button
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors"
                              onClick={() => handleRetry(payment.paymentId)}
                              title="Retry this payment"
                            >
                              <RotateCcw size={11} />
                              Retry
                            </button>
                          )}
                          <button
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium text-fintech-secondary bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors"
                            onClick={() => setSelectedPayment(payment)}
                            title="View details"
                          >
                            <Eye size={11} />
                            Details
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.pages > 1 && (
            <div className="px-6 py-4 border-t border-fintech-border flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-xs text-fintech-textMuted">
                Page <span className="font-semibold text-fintech-text">{pagination.page}</span> of{' '}
                <span className="font-semibold text-fintech-text">{pagination.pages}</span>
                {' '}· {pagination.total} total
              </p>
              <div className="flex items-center gap-1">
                <button
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                  className="p-1.5 rounded-lg text-fintech-textMuted hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label="Previous page"
                >
                  <ChevronLeft size={16} />
                </button>

                {Array.from({ length: Math.min(pagination.pages, 5) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors ${
                        currentPage === page
                          ? 'bg-fintech-primary text-white'
                          : 'text-fintech-textMuted hover:bg-slate-100'
                      }`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  disabled={currentPage === pagination.pages}
                  onClick={() => handlePageChange(currentPage + 1)}
                  className="p-1.5 rounded-lg text-fintech-textMuted hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label="Next page"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {selectedPayment && (
        <div
          className="modal-backdrop"
          onClick={() => setSelectedPayment(null)}
        >
          <div
            className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-fintech-border">
              <h3 className="font-semibold text-fintech-text">Payment Details</h3>
              <button
                className="p-1.5 rounded-lg text-fintech-textMuted hover:bg-slate-100 transition-colors"
                onClick={() => setSelectedPayment(null)}
                aria-label="Close details"
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5 space-y-3">
              {[
                { label: 'Transaction Ref', value: selectedPayment.transactionRef, mono: true },
                { label: 'Amount', value: formatCurrency(selectedPayment.amount), bold: true },
                { label: 'Created', value: formatDateTime(selectedPayment.createdAt) },
                {
                  label: 'Completed',
                  value: selectedPayment.completedAt ? formatDateTime(selectedPayment.completedAt) : '—',
                },
              ].map((row) => (
                <div key={row.label} className="flex items-start justify-between gap-4 py-2 border-b border-slate-50 last:border-0">
                  <span className="text-xs text-fintech-textMuted flex-shrink-0">{row.label}</span>
                  <span className={`text-xs text-right ${row.mono ? 'font-mono text-fintech-textMuted' : ''} ${row.bold ? 'font-bold text-fintech-text' : 'text-fintech-text'}`}>
                    {row.value}
                  </span>
                </div>
              ))}

              {/* Status */}
              <div className="flex items-center justify-between py-2">
                <span className="text-xs text-fintech-textMuted">Status</span>
                <span className={`badge ${(statusConfig[selectedPayment.status] || { badgeClass: 'badge-secondary' }).badgeClass}`}>
                  {selectedPayment.status}
                </span>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 pb-5">
              <button
                type="button"
                className="btn-secondary w-full justify-center text-sm"
                onClick={() => downloadReceipt(selectedPayment)}
              >
                <Download size={14} />
                Download Receipt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentHistory;
